<template>
    <div>
        <div role="status" aria-live="polite">
            <div class="sr-only">
                <span v-if="selectedStops.length > 0">Loading stops</span>
            </div>
        </div>
        <div v-if="routeStore.isLoading" class="flex justify-center w-full py-8">
            <Spinner class="w-8 h-8 text-primary"/>
        </div>

        <transition name="fade">
            <div v-if="currentRoute">
                <div class="p-2 border border-gray-100 rounded-lg">
                    <div class="flex flex-col-reverse w-full border-t-2 border-b-2 lg:flex-row border-primary">
                        <div class="relative flex w-full lg:w-[23rem] h-[51vh] justify-center overflow-hidden">
                            <div class="overflow-y-scroll border-2 border-transparent md:mx-24 lg:mx-0 lg:w-full w-5/6 focus:outline-none focus:border-secondary" tabindex="0" aria-label="Stop details scrollable content">
                                <MapsSchedulesStopInfo v-if="selectedStops.length > 0" :stops="selectedStops"
                                                       :route="currentRoute"/>
                                <div v-else class="p-6 text-base text-center text-gray-700">
                                    Select a stop for details.
                                </div>
                            </div>
                        </div>
                        <div class="flex-grow h-[51vh] min-h-[300px]">
                            <div class="w-full h-full overflow-hidden">
                                <RouteMap
                                    v-if="currentRoute && selectedSchedule?.shape"
                                    ref="map"
                                    class="w-full h-full"
                                    :route="currentRoute"
                                    :shape="mapShape"
                                    :zoom="mapZoom"
                                    :center-on-bounds="selectedStops.length === 0"
                                    :highlighted-stops="selectedStops"
                                    @ready="onMapReady"
                                    @onMarkerClicked="onMarkerClicked"
                                    hide-from-and-to
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import {computed, defineComponent, onMounted, ref} from 'vue';
import { setGoogleApi } from "../../utils/google";
import RouteMap from "../RouteMap";
import Spinner from "../Spinner";
import MapsSchedulesStopInfo from "./MapsSchedulesStopInfo";
import {useRouteStore} from "../../stores/routes";

export default defineComponent({
    name: 'MapsSchedulesRouteMap',
    components: {Spinner, RouteMap, MapsSchedulesStopInfo},
    setup() {
        const routeStore = useRouteStore();
        const selectedSchedule = computed(() => routeStore.selectedSchedule);
        const selectedStops = computed(() => routeStore.selectedStops);
        const currentRoute = computed(() => routeStore.routes.current);
        const map = ref(null);
        const mapZoom = ref(11);

        const mapShape = computed(() => {
            if (!selectedSchedule.value?.shape) {
                return null;
            }

            return {
                ...selectedSchedule.value.shape,
                stops: selectedSchedule.value.shape.allStops,
                originStop: null,
                destStop: null,
            };
        });

        function onMapReady(mapRef) {
            setGoogleApi(mapRef.value.api);

            if (selectedStops && selectedStops.value.length > 0) {
                const stop = selectedSchedule.value.shape.allStops.find((s) => s.code === selectedStops.value[0].code);
                if (stop) {
                    mapZoom.value = 16;
                    map.value.setMapCenter({
                        lat: parseFloat(stop.location.lat),
                        lng: parseFloat(stop.location.lng),
                    });
                }
            }
        }

        function onMarkerClicked(marker) {
            routeStore.setSelectedStops([marker.extra_options.stop]);
        }

        onMounted(async () => {
            await routeStore.initStore();
        })

        return {
            routeStore,
            map,
            mapZoom,
            mapShape,
            currentRoute,
            selectedStops,
            selectedSchedule,
            onMapReady,
            onMarkerClicked,
        }
    },
});
</script>
